// Theme utilities and provider for app-wide light/dark handling
import { Capacitor, SystemBars, SystemBarsStyle } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { type ReactNode, useEffect, useState } from "react";
import { type Theme, ThemeContext } from "./theme";

const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"

// Sync the native status bar style with the active theme.
// - Android: uses the Capacitor 8 SystemBars built-in (non-deprecated API).
// - iOS: uses @capacitor/status-bar (setBackgroundColor is not supported on iOS).
async function applyNativeStatusBar(isDark: boolean) {
  if (platform === "android") {
    await SystemBars.setStyle({
      style: isDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    });
  } else if (platform === "ios") {
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  }
}

// Determine initial theme from localStorage or system preference
function getInitialTheme(): Theme {
  const storedTheme = localStorage.getItem("theme") as Theme | null;

  if (storedTheme) {
    return storedTheme;
  }

  if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
}

// Provider that applies theme class to the document and exposes a toggle
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === "dark";

    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    localStorage.setItem("theme", theme);

    // Keep PWA status/nav bar colour in sync with the active theme.
    // The meta tags in index.html use prefers-color-scheme, but a user
    // may choose a theme that differs from their system preference, so we
    // update all theme-color meta tags here to match the real app state.
    const themeColor = isDark ? "#111827" : "#ffffff";
    for (const meta of document.querySelectorAll<HTMLMetaElement>(
      "meta[name='theme-color']",
    )) {
      meta.setAttribute("content", themeColor);
    }

    // Sync the native iOS/Android status bar with the active theme.
    void applyNativeStatusBar(isDark);
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      const hasStoredPreference = localStorage.getItem("theme");
      if (!hasStoredPreference) {
        setTheme(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
